import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import packageJson from '../../../package.json';
import buildInfo from '../../buildInfo.json';

export default function AppInformation() {
  const navigate = useNavigate();
  const [backendVersion, setBackendVersion] = useState<string>('...');
  const [environment, setEnvironment] = useState<string>('...');

  useEffect(() => {
    // Fetch backend version from health endpoint
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    fetch(`${API_BASE_URL}/health`)
      .then(res => res.json())
      .then(data => setBackendVersion(data.version || 'Unknown'))
      .catch(() => setBackendVersion('Error'));

    // Determine environment from URL
    const hostname = window.location.hostname;
    if (hostname.includes('staging')) {
      setEnvironment('Staging');
    } else if (hostname.includes('localhost')) {
      setEnvironment('Development');
    } else {
      setEnvironment('Production');
    }
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 theme-surface">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold theme-text">App Information</h1>
        <button
          onClick={() => navigate('/settings')}
          className="theme-text-secondary hover:theme-text-primary p-2 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="theme-surface border rounded-lg p-6">
        <div className="flex items-center justify-center mb-8">
          <svg className="w-16 h-16 text-healthy-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        <div className="space-y-4">
          <InfoRow label="Web Version" value={`v${packageJson.version}`} />
          <InfoRow label="Backend Version" value={`v${backendVersion}`} />
          <InfoRow label="Environment" value={environment} />
          <InfoRow label="Last Updated" value={buildInfo.lastUpdated} />
        </div>

        <div className="mt-8 pt-6 border-t theme-border-primary">
          <p className="text-sm theme-text-secondary text-center">
            © 2025 Advita Ortho. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between py-3 border-b theme-border-primary last:border-b-0">
      <span className="text-sm theme-text-secondary">{label}</span>
      <span className="text-sm font-semibold theme-text-primary">{value}</span>
    </div>
  );
}
